import { generateApiErrorPayload } from 'api-client/helpers'
import ModalForm from 'core/elements/modal/ModalForm'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isOpenCloud } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { useToast } from 'core/providers/ToastProvider'
import { MessageTypes } from 'core/components/notifications/model'
import { notificationActions, NotificationType } from 'core/notifications/notificationReducers'
import { isSelfServiceRole } from 'k8s/util/helpers'
import { listClusterHostGroups } from 'openstack/components/infrastructure/clusters/actions'
import { clusterHostGroupsSelector } from 'openstack/components/infrastructure/clusters/selectors'
import { prop } from 'ramda'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import useReactRouter from 'use-react-router'
import { trackEvent } from 'utils/tracking'
import { listHostAggregates } from '../infrastructure/actions'
import { listVolumeSnapshots } from '../storage/snapshots/actions'
import {
  volumeSnapshotsByIdSelector,
  volumeSnapshotsSelector,
} from '../storage/snapshots/selectors'
import {
  listAvailableVolumes,
  listNonPaginatedVolumes,
  pollAvailableVolumes,
  pollNonPaginatedVolumes,
} from '../storage/volumes/actions'
import {
  availableVolumesSelector,
  nonPaginatedVolumesByIdSelector,
  nonPaginatedVolumesSelector,
} from '../storage/volumes/selectors'
import { IVirtualMachinesListPageTabs } from './VirtualMachinesListPage'
import {
  CREATE_VM_AVAILABILITY_ZONE_ERROR,
  CREATE_VM_MODAL_TITLE,
  CreateVmFormParams,
  createDefaultCreateVmParams,
  getCreateVmStepNumber,
  getMinAvailableManagedNetwork,
  getSubnetsNeedingPorts,
  isWindowsVmSource,
  shouldForceSingleVmCount,
} from './CreateVirtualMachineModal.helpers'
import CreateVirtualMachineAttachVolumesStep from './createVirtualMachineSteps/AttachVolumesStep'
import CreateVirtualMachineCustomizeStep, {
  useCreateVirtualMachineCustomizeStepController,
} from './createVirtualMachineSteps/CustomizeStep'
import CreateVirtualMachineFlavorStep, {
  useCreateVirtualMachineFlavorStepController,
} from './createVirtualMachineSteps/FlavorStep'
import CreateVirtualMachineNetworkingStep, {
  useCreateVirtualMachineNetworkingStepController,
} from './createVirtualMachineSteps/NetworkingStep'
import CreateVirtualMachineSourceStep, {
  useCreateVirtualMachineSourceStepController,
} from './createVirtualMachineSteps/SourceStep'
import VmCredentialsPanel from './VmCredentialsPanel'
import { createVirtualMachine, createVirtualMachines } from './actions'
import {
  ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  useNetworkPorts,
  NetworkPortsResult,
} from './hooks/useNetworkPorts'
import useCreateVirtualMachineModalStyles from './CreateVirtualMachineModal.styles'
import { buildVMRequestBody } from './utils/vmBodyBuilder'
import { chameleonActions } from 'core/session/chameleonReducers'
import { withRetry } from 'utils/async'

export {
  getPrivateIpValidator,
  hotplugMemoryValidator,
  hotplugVcpuValidator,
  placeholderCloudInitYaml,
} from './CreateVirtualMachineModal.helpers'
export { ErrorText } from './CreateVirtualMachineModal.shared'

const modalTitle = CREATE_VM_MODAL_TITLE
const returnStepNumber = getCreateVmStepNumber

interface Props {
  addRoute: Route
  reloadVirtualMachines: () => void
}

export default function CreateVirtualMachineModal({ addRoute, reloadVirtualMachines }: Props) {
  const { history, match } = useReactRouter()
  const classes = useCreateVirtualMachineModalStyles()
  const dispatch = useDispatch()
  const showToast = useToast()

  // Network port management hook (handles both simple and managed networks)
  const { preCreatePorts, cleanupPreCreatedPorts } = useNetworkPorts()

  const [showVmPassword, setShowVmPassword] = useState(false)
  const [vmPassword, setVmPassword] = useState('')
  const [minAvailableIpsNetwork, setMinAvailableIpsNetwork] = useState(null)
  const [isoCreateError, setIsoCreateError] = useState(null)
  const [portCreationError, setPortCreationError] = useState<{
    title: string
    message: string
  } | null>(null)
  const [submitPending, setSubmitPending] = useState(false)
  const submitPendingRef = useRef(false)
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features } = session
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'
  // If boot_from_volume is false, disable the boot from volume option, undefined or any other value means it is enabled
  const disabledBootFromVolume = features?.experimental?.boot_from_volume === false

  const defaultParams = createDefaultCreateVmParams(disabledBootFromVolume)
  const { prefs } = useScopedPreferences()
  const currentTenant = prefs.currentTenant as string
  const { params, getParamsUpdater, setParams, updateParams } = useParams<CreateVmFormParams>(
    defaultParams,
  )
  const isSelfServiceUser = isSelfServiceRole(session)
  const isOpen = addRoute.pattern.match(match.url)

  const { loading: loadingVolumes } = useListAction(listNonPaginatedVolumes, {
    params: {},
  })
  const volumes = useSelector(nonPaginatedVolumesSelector)

  // Available volumes list to show for Existing Volume source type and Attach Volumes list
  const { loading: loadingAvailableVolumes, reload: reloadAvailableVolumes } = useListAction(
    listAvailableVolumes,
    {
      loadOnDemand: true,
    },
  )
  const { updating: updatingAvailableVolumes, update: updateAvailableVolumes } = useUpdateAction(
    pollAvailableVolumes,
  )
  const { update: updateNonPaginatedVolumes } = useUpdateAction(pollNonPaginatedVolumes)
  const availableVolumesRaw = useSelector(availableVolumesSelector)

  const volumesById = useSelector(nonPaginatedVolumesByIdSelector)

  const { loading: loadingVolumeSnapshots } = useListAction(listVolumeSnapshots, {
    params: {},
  })
  const volumeSnapshots = useSelector(volumeSnapshotsSelector)
  const volumeSnapshotsById = useSelector(volumeSnapshotsByIdSelector)

  const sourceStep = useCreateVirtualMachineSourceStepController({
    availableVolumesRaw,
    currentTenant,
    isOpen,
    loadingAvailableVolumes,
    loadingVolumeSnapshots,
    loadingVolumes,
    params,
    reloadAvailableVolumes,
    updatingAvailableVolumes,
    volumeSnapshots,
    volumeSnapshotsById,
    volumesById,
  })
  const flavorStep = useCreateVirtualMachineFlavorStepController({ params })
  const networkingStep = useCreateVirtualMachineNetworkingStepController({
    currentTenant,
    isSelfServiceUser,
  })
  const customizeStep = useCreateVirtualMachineCustomizeStepController()

  useListAction(listHostAggregates)

  const { update, updating, error, reset } = useUpdateAction(createVirtualMachine)
  const {
    update: createMultiple,
    updating: creatingMultiple,
    error: errorMultiple,
    reset: resetMultiple,
  } = useUpdateAction(createVirtualMachines)
  const responseError = error || errorMultiple

  const { loading: loadingClusterHostGroups } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // After everything is loaded, track object numbers
    if (
      !sourceStep.loadingImages &&
      !loadingVolumes &&
      !flavorStep.loadingFlavors &&
      !networkingStep.loadingNetworks &&
      !loadingClusterHostGroups
    ) {
      const body = {
        images: sourceStep.images?.length,
        volumes: volumes?.length,
        flavors: flavorStep.flavors?.length,
        networks: networkingStep.networksList?.length,
        networksWithFreeIps: networkingStep.networksList?.filter(
          (network) => network?.availableIps > 0,
        )?.length,
        clusters: clusterHostGroups?.length,
      }
      trackEvent(`${modalTitle} - Resources Present`, body)
    }
  }, [
    sourceStep.loadingImages,
    loadingVolumes,
    flavorStep.loadingFlavors,
    networkingStep.loadingNetworks,
    loadingClusterHostGroups,
    match.url,
  ])

  // Manual collection refreshes to get latest
  useEffect(() => {
    if (!isOpen) {
      return
    }
    // Reload available volumes to ensure we have the latest volumes
    updateAvailableVolumes()
    // Reload non-paginated volumes to ensure we have the latest volumes
    updateNonPaginatedVolumes()
    // Reload tenant config and source-step resources
    sourceStep.reloadSourceStepResources()
    // Reload networks to ensure we have the latest networks
    networkingStep.reloadNetworks(true)
  }, [match.url]) // Do not use isOpen as dependency, that object constantly changes

  useEffect(() => {
    if (shouldForceSingleVmCount(params)) {
      updateParams({
        count: 1,
      })
    }
  }, [params.sourceType, params.attachVolumes])

  const releaseSubmitLock = useCallback(() => {
    submitPendingRef.current = false
    setSubmitPending(false)
  }, [])

  const getVolumeSnapshotSourceVolume = (volumeSnapshot) => {
    return volumesById[volumeSnapshot?.volume_id]
  }

  /**
   * Create batch VMs in parallel with per-VM retry.
   * Supports both simple and managed network ports.
   * FR-013: If hostname provided, applies hostname-<index> suffix to each VM.
   */
  const createBatchVMsInParallel = async (portsByVm: NetworkPortsResult[]): Promise<void> => {
    const results = await Promise.allSettled(
      Array.from({ length: params.count }, async (_, i) => {
        const vmPorts = portsByVm[i] || { simple: {}, managed: {} }
        const vmName = params.count > 1 ? `${params.name}-${i + 1}` : params.name
        const vmHostname =
          params.hostname && params.count > 1 ? `${params.hostname}-${i + 1}` : params.hostname
        const body = buildVMRequestBody(
          { ...params, count: 1, name: vmName, hostname: vmHostname } as any,
          { simpleNetworkPorts: vmPorts.simple, managedNetworkPorts: vmPorts.managed },
        )
        return withRetry(
          async () => {
            // Call action directly for parallel execution (hook would serialize)
            // If no error is thrown, the VM was created successfully
            const response = (await createVirtualMachine.call(
              { body },
              { propagateError: true },
            )) as any
            // createVirtualMachine action returns the VM object with id and adminPass
            // If call() succeeded (no exception), we have a valid response
            const vmId = response?.id
            if (!vmId) {
              console.error('VM creation response missing id:', response)
              throw new Error('VM creation response missing ID')
            }
            return { vmName, id: vmId }
          },
          2,
          5000,
        )
      }),
    )

    const successes: string[] = []
    const failures: { vmName: string; error: string }[] = []

    await Promise.all(
      results.map(async (result, i) => {
        const vmPorts = portsByVm[i] || { simple: {}, managed: {} }
        const vmName = params.count > 1 ? `${params.name}-${i + 1}` : params.name
        if (result.status === 'fulfilled') {
          successes.push(result.value.id)
          console.debug(`[Batch] VM ${vmName} created successfully (attempt succeeded)`)
        } else {
          failures.push({
            vmName,
            error: result.reason?.message || 'Unknown error',
          })
          console.warn(
            `[Batch] VM ${vmName} creation failed after retries:`,
            result.reason?.message,
          )
          await cleanupPreCreatedPorts({ ...vmPorts.simple, ...vmPorts.managed })
        }
      }),
    )

    if (successes.length > 0) {
      trackEvent(`${modalTitle} - Finished`, {
        vmIds: successes,
        batchSize: params.count,
        failureCount: failures.length,
      })
      reloadVirtualMachines()
      handleClose()

      if (failures.length === 0) {
        showToast(`${successes.length} VM(s) created successfully.`, MessageTypes.success)
      } else {
        showToast(
          `${successes.length}/${params.count} VMs created. ${failures.length} failed.`,
          MessageTypes.warning,
        )
        dispatch(
          notificationActions.registerNotification({
            title: `Batch VM creation: ${failures.length} failure(s)`,
            message: failures.map((f) => `${f.vmName}: ${f.error}`).join('\n'),
            type: NotificationType.error,
            data: { successCount: successes.length, failureCount: failures.length },
          }),
        )
      }
    } else {
      dispatch(
        notificationActions.registerNotification({
          title: `Batch VM creation failed (${params.count} VMs)`,
          message: failures.map((f) => `${f.vmName}: ${f.error}`).join('\n'),
          type: NotificationType.error,
          data: { totalCount: params.count, failureCount: failures.length },
        }),
      )
    }
  }

  const submitForm = useCallback(async () => {
    if (submitPendingRef.current) {
      return
    }
    submitPendingRef.current = true
    setSubmitPending(true)
    setIsoCreateError(null)
    try {
      // Check minimum available IPs from all selected MANAGED networks
      // For example, if 5 networks are selected with 1 available IP each,
      // the limiting factor is the smallest available IP (1), so we can only create 1 VM in this case.
      // NOTE: Simple Networks are excluded from this check as they pre-create ports
      const networkWithMinIps = getMinAvailableManagedNetwork(params?.networks)

      if (networkWithMinIps) {
        setMinAvailableIpsNetwork(networkWithMinIps)
        // If the smallest available IPs across MANAGED networks is less than the VM count, show error
        if (networkWithMinIps?.availablePortAndIPs < params.count) {
          dispatch(
            notificationActions.registerNotification({
              title: 'Insufficient IP addresses',
              message: `${networkWithMinIps.name} has only ${networkWithMinIps?.availablePortAndIPs} available IP(s), but ${params.count} VMs requested.`,
              type: NotificationType.error,
              data: {
                requestedCount: params.count,
                availableIPs: networkWithMinIps?.availablePortAndIPs,
              },
            }),
          )
          return
        }
      } else {
        setMinAvailableIpsNetwork(null)
      }

      // Pre-create ports for both Simple Networks and Managed Networks with selected subnets
      const emptyPorts: NetworkPortsResult = { simple: {}, managed: {} }
      let preCreatedPorts: NetworkPortsResult | NetworkPortsResult[] = emptyPorts
      const hasSimpleNetworks = params.networks.some((network) => network.isSimpleNetwork)
      // Only pre-create subnet ports where the user left Ports = Automatic; an explicit
      // port or Private IP must take precedence (PCD-8480).
      const subnetsNeedingPorts = getSubnetsNeedingPorts(
        params.selectedSubnets,
        params.selectedPorts,
      )
      const hasManagedSubnetSelections = Object.keys(subnetsNeedingPorts).length > 0

      if (hasSimpleNetworks || hasManagedSubnetSelections) {
        try {
          preCreatedPorts = await preCreatePorts(
            params.networks,
            subnetsNeedingPorts,
            currentTenant,
            params.name,
            params.count,
          )
        } catch (error) {
          console.error('Port pre-creation failed:', error)
          // Extract error message using API error payload utility
          const errorPayload = generateApiErrorPayload(error, '', 'Failed to create network ports')
          const neutronError =
            (error as any)?.response?.data?.NeutronError || (error as any)?.NeutronError
          const errorTitle = neutronError?.type || 'Port pre-creation failed'

          setPortCreationError({
            title: errorTitle,
            message: errorPayload?.message || 'Failed to create network ports',
          })
          return
        }
      }

      // Unified batch creation: VMs are created IN PARALLEL via Promise.allSettled with per-VM retry.
      // Supports managed subnet ports, simple networks (when flag enabled), and hostname suffix.
      // FR-013: If hostname provided, applies hostname-<index> suffix to each VM.
      const needsSimpleNetworkBatchCreation =
        ENABLE_SIMPLE_NETWORK_BATCH_CREATION && hasSimpleNetworks && params.count > 1
      const needsBatchCreation =
        params.count > 1 &&
        (hasManagedSubnetSelections || needsSimpleNetworkBatchCreation || params.hostname)

      if (needsBatchCreation) {
        const portsByVm: NetworkPortsResult[] = Array.isArray(preCreatedPorts)
          ? preCreatedPorts
          : Array.from({ length: params.count }, () => ({ simple: {}, managed: {} }))
        return await createBatchVMsInParallel(portsByVm)
      }

      const ports = preCreatedPorts as NetworkPortsResult
      const cleanupAllPorts = () => cleanupPreCreatedPorts({ ...ports.simple, ...ports.managed })
      const hasPreCreatedPorts =
        Object.keys(ports.simple).length > 0 || Object.keys(ports.managed).length > 0

      try {
        // Build VM request body using shared utility
        const body = buildVMRequestBody(params as any, {
          simpleNetworkPorts: ports.simple,
          managedNetworkPorts: ports.managed,
        })
        const updateFn = params.count === 1 ? update : createMultiple
        const { success, response } = await updateFn({ body })
        if (success) {
          const eventBody =
            params.count > 1
              ? {
                  vmIds: response?.map((res) => res?.id || 'N/A'),
                }
              : {
                  vmId: response?.id,
                }
          // Track the created VM ID to check later to see if it is active
          if (params.count === 1) {
            dispatch(
              chameleonActions.updateChameleonState({
                createdVmId: response?.id,
              }),
            )
          }
          trackEvent(`${modalTitle} - Finished`, eventBody)
          reloadVirtualMachines()
          if (params.count > 1) {
            handleClose()
            return
          }
          // If not a windows VM, also close the wizard
          const isWindows = isWindowsVmSource(params, getVolumeSnapshotSourceVolume)
          if (!isWindows) {
            handleClose()
            return
          }
          setVmPassword(response?.adminPass)
          setShowVmPassword(true)
        } else {
          trackEvent(`${modalTitle} - Create API Failure`)
          // Cleanup pre-created ports if VM creation failed
          if (hasPreCreatedPorts) await cleanupAllPorts()
        }
      } catch (vmCreationError) {
        console.error('VM creation failed:', vmCreationError)
        // Cleanup pre-created ports if VM creation failed
        if (hasPreCreatedPorts) await cleanupAllPorts()
        if (params.sourceType === 'installFromIso') {
          const message =
            generateApiErrorPayload(vmCreationError, '', 'Failed to create VM from ISO')?.message ||
            vmCreationError?.message ||
            'Failed to create VM from ISO'
          setIsoCreateError({ title: '', message })
          return
        }
        // Re-throw to show error to user
        throw vmCreationError
      }
    } finally {
      releaseSubmitLock()
    }
  }, [params, currentTenant, volumesById, releaseSubmitLock])

  const handleClose = () => {
    history.push(
      routeManager.routes.openstack.vms.path({
        vmstab: IVirtualMachinesListPageTabs.VirtualMachinesList,
      }),
    )
    setShowVmPassword(false)
    setVmPassword('')
    setIsoCreateError(null)
    setPortCreationError(null)
    releaseSubmitLock()
    reset()
    resetMultiple()
    sourceStep.resetSourceStepState()
    flavorStep.resetFlavorStepState()
    networkingStep.resetNetworkingStepState()
    setParams(defaultParams)
  }

  const isClusterRequired = !(isVmware && isOpenCloud())

  useEffect(() => {
    if (showVmPassword) {
      const modalBody = document.querySelector('.modal-body')
      modalBody.scrollTo(0, 0)
    }
  }, [showVmPassword])

  return (
    <ModalForm
      route={addRoute}
      title={modalTitle}
      onSubmit={showVmPassword ? handleClose : submitForm}
      onClose={handleClose}
      submitting={updating || creatingMultiple || submitPending}
      disableSubmit={(step) =>
        params.sourceType === 'installFromIso' && step === 1 && !params.installerIso?.[0]
      }
      disableSubmitMessage={(step) =>
        params.sourceType === 'installFromIso' && step === 1 && !params.installerIso?.[0]
          ? 'Please select an installer ISO to continue.'
          : ''
      }
      steps={returnStepNumber(params.sourceType, 5)}
      error={
        portCreationError ||
        isoCreateError ||
        (responseError?.message === CREATE_VM_AVAILABILITY_ZONE_ERROR
          ? {
              message: `The selected cluster '${params.availabilityZone}' does not have any hosts assigned. Please assign a host to it or select a different cluster`,
            }
          : responseError)
      }
      submitTitle={showVmPassword ? `Finish` : `Deploy Virtual Machine`}
      hideBack={showVmPassword}
      maxWidth={900}
      showConfirmationOnCancel
    >
      {({ step }) => (
        <>
          {!showVmPassword ? (
            <>
              {step === 1 && (
                <CreateVirtualMachineSourceStep
                  {...sourceStep}
                  getParamsUpdater={getParamsUpdater}
                  isClusterRequired={isClusterRequired}
                  params={params}
                  updateParams={updateParams}
                />
              )}
              {step === 2 && !['image', 'snapshot'].includes(params.sourceType) && (
                <CreateVirtualMachineAttachVolumesStep
                  availableVolumesRaw={availableVolumesRaw}
                  getParamsUpdater={getParamsUpdater}
                  loadingAvailableVolumes={loadingAvailableVolumes}
                  params={params}
                  reloadAvailableVolumes={reloadAvailableVolumes}
                  updatingAvailableVolumes={updatingAvailableVolumes}
                />
              )}
              {step === returnStepNumber(params.sourceType, 3) && (
                <CreateVirtualMachineFlavorStep
                  {...flavorStep}
                  params={params}
                  updateParams={updateParams}
                />
              )}
              {step === returnStepNumber(params.sourceType, 4) && (
                <CreateVirtualMachineNetworkingStep
                  {...networkingStep}
                  getParamsUpdater={getParamsUpdater}
                  params={params}
                  setMinAvailableIpsNetwork={setMinAvailableIpsNetwork}
                  updateParams={updateParams}
                />
              )}
              {step === returnStepNumber(params.sourceType, 5) && (
                <CreateVirtualMachineCustomizeStep
                  {...customizeStep}
                  getParamsUpdater={getParamsUpdater}
                  minAvailableIpsNetwork={minAvailableIpsNetwork}
                  params={params}
                  updateParams={updateParams}
                  vmNetworkMode={networkingStep.vmNetworkMode}
                />
              )}
            </>
          ) : (
            <div className={classes.passwordScreen}>
              <VmCredentialsPanel vmPassword={vmPassword} />
            </div>
          )}
        </>
      )}
    </ModalForm>
  )
}
